import {
  ALWAYS_VISIBLE_MENU,
  MENU_UI,
  MENU_KEY_ALIAS,
  MODULE_UI,
} from '@/constants/menuCatalog';
import { permissionMatrixRow } from '@/constants/permissionMatrix';
import { useIntl } from '@/i18n';
import { preloadRoute } from '@/router/preload';
import { useAuthStore } from '@/store/auth';
import { useModuleGateStore } from '@/store/moduleGate';
import { renderIcon } from '@/utils/icon';
import {
  isModuleGated,
  moduleGateReasonOf,
  type ModuleGateReason,
} from '@/utils/moduleGate';
import type { MenuProps } from 'antd';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';

type MenuItems = Required<MenuProps>['items'];

/**
 * 侧边栏/面包屑共用的展示节点。三种层级共用同一个形状：
 * - module：一级模块（`acct` / `member`…），键取 `MODULE_UI` 的模块代码
 * - group：二级分组标题（不可点，antd 的 `type: 'group'`），键是
 *   `${模块代码}::${分组代码}`——分组不是权限键，只用来拼一个内部展示 key
 * - page：三级页面，键就是服务端 / 路由 `handle.access` 用的权限键（如 `org.notice`）
 */
export interface MenuUINode {
  code: string;
  kind: 'module' | 'group' | 'page';
  /** 只有 page 节点有路由地址 */
  path?: string;
  icon?: string;
  title: string;
  children?: MenuUINode[];
  /** 排序权重，构树时用；不进最终渲染（build() 不读这个字段） */
  order?: number;
  /**
   * 所属模块当前是否锁着（本会档位不够 / 期次未到）。
   * 模块与页面节点都带：模块看一级栏，页面看二级栏——页面按所属模块判，不单独判。
   */
  gated?: boolean;
  /** 锁的原因；`gated` 为假时为 null */
  reason?: ModuleGateReason | null;
}

function flattenNodes(nodes: MenuUINode[]): MenuUINode[] {
  return nodes.flatMap((node) => [
    node,
    ...(node.children?.length ? flattenNodes(node.children) : []),
  ]);
}

/** 模块/分组下的第一个页面，仅用于悬停预取。 */
export function firstPagePath(node: MenuUINode): string | undefined {
  if (node.kind === 'page') return node.path;
  for (const child of node.children ?? []) {
    const found = firstPagePath(child);
    if (found) return found;
  }
  return undefined;
}

/**
 * 展示树 → antd Menu 的 items。
 *
 * 页面项挂 `onMouseEnter`、模块项挂 `onTitleMouseEnter`：鼠标移上去就把这页的 chunk
 * 提前拉下来（见 `router/preload.ts`）。分组标题只是标题、不承载页面，不挂。
 * 侧边栏与模块栏共用这一份，`items` 与 `moduleItems` 不会各长一套悬停逻辑。
 */
function buildMenuItems(nodes: MenuUINode[]): MenuItems {
  return nodes.map((node) => {
    const children = node.children?.length
      ? buildMenuItems(node.children)
      : undefined;
    if (node.kind === 'group') {
      return { key: node.code, type: 'group' as const, label: node.title, children };
    }
    const prefetch = () =>
      preloadRoute(node.kind === 'page' ? node.path : firstPagePath(node));
    return {
      key: node.code,
      icon: renderIcon(node.icon),
      label: node.title,
      children,
      // 置灰不是隐藏（PRD 27669）：锁着的页照旧列出，样式在 global.css
      className: node.gated ? 'admin-menu-item-locked' : undefined,
      ...(node.kind === 'module'
        ? { onTitleMouseEnter: prefetch }
        : { onMouseEnter: prefetch }),
    };
  });
}

/** 在展示树里按条件查找从根到目标的链路，用于面包屑与 openKeys */
function findNodePath(
  nodes: MenuUINode[],
  predicate: (node: MenuUINode) => boolean,
  parents: MenuUINode[] = [],
): MenuUINode[] | null {
  for (const node of nodes) {
    if (predicate(node)) return [...parents, node];
    if (node.children?.length) {
      const found = findNodePath(node.children, predicate, [...parents, node]);
      if (found) return found;
    }
  }
  return null;
}

/**
 * 恒显项显示判据：工作台是唯一白名单，其余必须有页面码且该页可达。
 *
 * 超管只免「可达」这一项（`canEnter` 同款通配），没有页面码的占位页（`order.create`）
 * 照旧不显示——那是登记表缺码，不是权限问题。
 */
function canShowAlwaysVisible(
  code: string,
  superAdmin: boolean,
  menus: Set<string>,
): boolean {
  if (code === 'home') return true;
  if (!permissionMatrixRow(code)?.pageCode) return false;
  return superAdmin || menus.has(code);
}

/**
 * 侧边栏菜单 = （能进的页面键 ∩ 前端的展示属性表）∪ 可达的恒显菜单项。
 *
 * 能进哪些页由服务端给：`reachableRoutes` 译成菜单键（`store/auth.ts` 的 `menus`）。
 * 超管（服务端 `superAdmin` 为真）看 `MENU_UI` 里**全部**页面入口——「全权」在界面上的
 * 落地，与 `useAccess` 同一个判据。名字走三语词条（见 `titleOf` 与 `menuCatalog.ts` 顶部），
 * 路径/图标/排序来自 `MENU_UI`。
 *
 * 服务端给了但前端没配 UI 的键会被跳过——那是「后端已实现、前端还没建页面」的
 * 正常状态，不是 bug。反之，前端有 UI 而服务端没给的页面（非超管）不显示。
 *
 * `ALWAYS_VISIBLE_MENU`（工作台 + 订单四页 + 后台帐号两页）不参与 `menus ∩ MENU_UI`
 * 的交集，但除工作台外仍要过「有页面码 + 该页可达」两道：否则同组兄弟会跟着常显项
 * 一起冒出来，出现「菜单能看见、接口 403」。
 */
export default function useMenu() {
  const menus = useAuthStore((s) => s.menus);
  const superAdmin = useAuthStore((s) => s.superAdmin);
  // 订阅整份 rows：门控刷新后菜单要跟着重算锁标记（gated/reason 是纯函数，读同一份）
  const gateRows = useModuleGateStore((s) => s.rows);
  const intl = useIntl();
  const location = useLocation();

  const getTitle = (node?: { title?: string }) => node?.title ?? '';

  /**
   * 页面名：词条 `menu.<键>`，取不到时回键本身（开发期一眼看得出漏了词条）。
   * 侧边栏、面包屑、页头标题、占位页、页内跳转文案（`{page}` 占位）共用这一份，
   * 页面名不再另开词条
   */
  const titleOf = useCallback(
    (code: string) =>
      intl.formatMessage({ id: `menu.${code}`, defaultMessage: code }),
    [intl],
  );

  const menuTree = useMemo<MenuUINode[]>(() => {
    // 一级模块 → 二级分组 → 页面
    const modules = new Map<string, Map<string, MenuUINode[]>>();

    const pushPage = (moduleCode: string, groupCode: string, node: MenuUINode) => {
      if (!modules.has(moduleCode)) modules.set(moduleCode, new Map());
      const groups = modules.get(moduleCode)!;
      if (!groups.has(groupCode)) groups.set(groupCode, []);
      groups.get(groupCode)!.push(node);
    };

    // 超管看 MENU_UI 里全部页面入口；其余人只看服务端给得进的那几页。两处都只认
    // 服务端那个布尔，不按 pageCount / 邮箱 / 本地标志推。
    // 合并页（日志管理）按 MENU_KEY_ALIAS 收成一项：三个页签级权限键同时存在时只出一条。
    const visibleKeys = [
      ...new Set(
        (superAdmin ? Object.keys(MENU_UI) : [...menus]).map(
          (key) => MENU_KEY_ALIAS[key] ?? key,
        ),
      ),
    ];

    visibleKeys.forEach((key) => {
      const ui = MENU_UI[key];
      if (!ui) return; // 前端还没建这一页，跳过
      // 常显项下面会无条件并入、挂在它自己声明的模块下；服务端也下发时不再按键前缀挂第二份
      if (ALWAYS_VISIBLE_MENU.some((item) => item.code === key)) return;
      // 归属模块默认按权限键前缀推断；`cert.*` 要落到会员中心的「证书管理」分组，
      // 靠 MENU_UI 的 module 覆盖（前缀是权限词的分段，不等于 PRD 的一级模块）
      const moduleCode = ui.module ?? key.split('.')[0];
      if (!MODULE_UI[moduleCode]) return; // 未知模块，跳过
      pushPage(moduleCode, ui.group ?? '', {
        code: key,
        kind: 'page',
        path: ui.path,
        icon: ui.icon,
        order: ui.order,
        title: titleOf(key),
        gated: isModuleGated(gateRows, moduleCode),
        reason: moduleGateReasonOf(gateRows, moduleCode),
      });
    });

    // 常显菜单项：工作台恒显；其余要有页面码且该页可达（见 canShowAlwaysVisible）
    const standaloneNodes: MenuUINode[] = [];
    ALWAYS_VISIBLE_MENU.forEach((item) => {
      if (!canShowAlwaysVisible(item.code, superAdmin, menus)) return;
      const node: MenuUINode = {
        code: item.code,
        kind: 'page',
        path: item.path,
        icon: item.icon,
        order: item.order,
        title: titleOf(item.code),
        gated: isModuleGated(gateRows, item.module),
        reason: moduleGateReasonOf(gateRows, item.module),
      };
      if (!item.module) {
        standaloneNodes.push(node);
        return;
      }
      if (!MODULE_UI[item.module]) return; // 未知模块，跳过
      pushPage(item.module, item.group ?? '', node);
    });

    // 门控模块置灰不隐藏（PRD 27669；原型 27673 对 gated 模块跳过 canSee 过滤）：
    // 非超管在这个模块下没有任何页面权限时，也要出一级模块节点（children 为空），
    // 否则「没买 + 没授权」的人整个模块都看不见，比灰着更让人以为点错了。
    // 没被门控的模块保持原行为——一页都进不去就不显示。
    Object.keys(MODULE_UI).forEach((moduleCode) => {
      if (modules.has(moduleCode)) return;
      if (!isModuleGated(gateRows, moduleCode)) return;
      modules.set(moduleCode, new Map());
    });

    const moduleNodes = [...modules.entries()].map(([moduleCode, groups]) => {
      const groupEntries = [...groups.entries()]
        .map(([groupCode, pages]) => {
          const sortedPages = [...pages].sort(
            (a, b) => (a.order ?? 0) - (b.order ?? 0),
          );
          // 分组本身没有显式排序字段，取组内最小的页面 order 稳定排序
          const order = Math.min(...sortedPages.map((p) => p.order ?? 0));
          return { groupCode, sortedPages, order };
        })
        .sort((a, b) => a.order - b.order);

      // 没有分组时不套一层，直接把页面挂在模块下
      const children = groupEntries.flatMap(({ groupCode, sortedPages }) =>
        groupCode
          ? [
              {
                code: `${moduleCode}::${groupCode}`,
                kind: 'group' as const,
                title: intl.formatMessage({
                  id: `menu.group.${moduleCode}.${groupCode}`,
                }),
                children: sortedPages,
              },
            ]
          : sortedPages,
      );

      return {
        code: moduleCode,
        kind: 'module' as const,
        icon: MODULE_UI[moduleCode].icon,
        title: intl.formatMessage({ id: `menu.module.${moduleCode}` }),
        children,
        order: MODULE_UI[moduleCode].order,
        gated: isModuleGated(gateRows, moduleCode),
        reason: moduleGateReasonOf(gateRows, moduleCode),
      };
    });

    return [...moduleNodes, ...standaloneNodes].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
  }, [menus, superAdmin, intl, titleOf, gateRows]);

  const items = useMemo<MenuItems>(() => buildMenuItems(menuTree), [menuTree]);

  const moduleItems = useMemo<Record<string, MenuItems>>(() => {
    return menuTree
      .filter((node) => node.kind === 'module')
      .reduce<Record<string, MenuItems>>((result, node) => {
        result[node.code] = buildMenuItems(node.children ?? []);
        return result;
      }, {});
  }, [menuTree]);

  /** 权限键 → 节点，只用于 SideMenu 点击跳转；页面名一律走 titleOf */
  const menuMap = useMemo(() => {
    return flattenNodes(menuTree).reduce<Record<string, MenuUINode>>(
      (memo, node) => {
        memo[node.code] = node;
        return memo;
      },
      {},
    );
  }, [menuTree]);

  /** 当前选中的菜单键以及需要展开的父级键 */
  const { selectedKeys, openKeys } = useMemo(() => {
    const search = new URLSearchParams(location.search);
    // 外链内嵌打开时用 query 上的 code 高亮菜单
    const codeFromQuery = search.get('code');
    const pages = flattenNodes(menuTree).filter((node) => node.kind === 'page');

    let matched: MenuUINode | undefined;
    if (codeFromQuery) {
      matched = pages.find((node) => node.code === codeFromQuery);
    }
    if (!matched) {
      matched = pages.find((node) => node.path === location.pathname);
    }
    if (!matched) {
      // 详情页等场景：取路径前缀最长的页面
      matched = pages
        .filter(
          (node) => node.path && location.pathname.startsWith(`${node.path}/`),
        )
        .sort((a, b) => (b.path?.length ?? 0) - (a.path?.length ?? 0))[0];
    }
    if (!matched) return { selectedKeys: [] as string[], openKeys: [] as string[] };

    const chain = findNodePath(menuTree, (node) => node.code === matched!.code) ?? [];
    return {
      selectedKeys: [matched.code],
      openKeys: chain.slice(0, -1).map((node) => node.code),
    };
  }, [menuTree, location.pathname, location.search]);

  /** 面包屑链路：模块 › 分组 › 页面 */
  const breadcrumbs = useMemo(() => {
    if (!selectedKeys.length) return [] as MenuUINode[];
    return findNodePath(menuTree, (node) => node.code === selectedKeys[0]) ?? [];
  }, [menuTree, selectedKeys]);

  return {
    items,
    moduleItems,
    menuTree,
    menuMap,
    selectedKeys,
    openKeys,
    breadcrumbs,
    getTitle,
    titleOf,
  };
}
